import { inject, Injectable, InjectionToken } from '@angular/core';
import { Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { IAttachmentSource } from './iattachment-source';

export interface ISalesforceMappingEntry {
  objectName: string;
  label?: string;
  relatedObjects?: { objectName: string; relationSegment: string }[];
}

export const SF_MAPPING_ENTRIES = new InjectionToken<Observable<ISalesforceMappingEntry[]>>('SF_MAPPING_ENTRIES');

@Injectable({ providedIn: 'root' })
export class SalesforceAttachmentSource implements IAttachmentSource {
  private mappingEntries$ = inject(SF_MAPPING_ENTRIES, { optional: true }) ?? of([]);

  getDefault(sourceEntity: string): string {
    return sourceEntity;
  }

  getOptions(sourceEntity: string): Observable<string[]> {
    // Salesforce attachment paths are plain SF object API names (not dotted like Bullhorn).
    // This matches what the backend Salesforce datasource adapter expects
    // (2-part path: [objectApiName, recordId]). The relationSegment traversal
    // is resolved at runtime from the relatedObjects mapping by the client,
    // not encoded in the stored path itself.
    return this.mappingEntries$.pipe(
      switchMap((entries) => {
        const mapping = entries.find((e) => e.objectName === sourceEntity);
        const related = mapping?.relatedObjects ?? [];
        const paths = [sourceEntity, ...related.map((r) => r.objectName)];
        return of(paths);
      }),
      map((paths) => [...new Set(paths)])
    );
  }

  getPathLabel(path: string): Observable<string | undefined> {
    if (!path) {
      return of(undefined);
    }
    return this.mappingEntries$.pipe(
      map((entries) => entries.find((e) => e.objectName === path)?.label ?? path)
    );
  }
}
